/**
 * App Registration tab — register client applications, mint integration keys,
 * fetch, reroll, and delete secure tenant credentials.
 *
 * @module panels/shuttle/apps
 */
import * as api from "../../api/shuttle.ts";
import { fmtDate } from "../../core/ui.ts";
import {
	injectRegistryStyles,
	toast,
} from "../../features/new-dashboard/registry-ui.ts";
import type { TenantApp } from "../../types/shuttle.ts";
import type { TabContext } from "./index.ts";

let lastTenantSlug = "";
let apps: TenantApp[] = [];
let mintedSecret: {
	key: string;
	appId: string;
	identifier: string;
} | null = null;
let selectedAppId: string | null = null;
let activeModal: "fetch" | "reroll" | "delete" | null = null;
let modalError = "";
let fetchedKeys: Record<string, string> = {};

export async function renderAppsTab(
	root: HTMLElement,
	ctx: TabContext,
): Promise<void> {
	const slug = ctx.tenantSlug;
	if (slug !== lastTenantSlug) {
		lastTenantSlug = slug;
		apps = [];
		mintedSecret = null;
		selectedAppId = null;
		activeModal = null;
		modalError = "";
		fetchedKeys = {};
	}

	const loadAndRender = async () => {
		try {
			apps = await api.listApps(slug);
		} catch (e) {
			root.innerHTML = `<div class="shuttle-banner error">Failed to load apps: ${escapeHtml((e as Error).message)}</div>`;
			return;
		}
		renderUI();
	};

	const renderUI = () => {
		injectRegistryStyles();

		const bannerHtml = mintedSecret
			? `
			<div style="background: rgba(34,197,94,.08); border: 1px solid rgba(34,197,94,.3); border-radius: var(--radius); padding: 16px; margin-bottom: var(--gap); display: flex; flex-direction: column; gap: 10px; box-shadow: 0 4px 12px rgba(34,197,94,.05);">
				<div style="display: flex; align-items: center; gap: 8px;">
					<span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: var(--c-green); box-shadow: 0 0 8px var(--c-green);"></span>
					<strong style="color: var(--c-green); font-size: 13px; text-transform: uppercase; letter-spacing: .5px;">Integration key created successfully</strong>
				</div>
				<div style="font-size: 12px; color: var(--c-text-muted);">
					This secret key will only be displayed <strong>once</strong>. Copy and store it securely in a safe place.
				</div>
				<div style="display: flex; align-items: center; gap: 8px; margin-top: 4px; background: rgba(0,0,0,0.15); border: 1px solid var(--c-border); padding: 4px 8px; border-radius: var(--radius);">
					<code style="font-family: var(--font-mono); font-size: 13px; color: var(--c-text); flex: 1; word-break: break-all; padding: 4px 8px;">${escapeHtml(mintedSecret.key)}</code>
					<button id="copy-key" class="reg-btn" style="background: var(--c-green); border-color: var(--c-green); color: #fff; padding: 6px 12px; font-size: 12px;">Copy</button>
				</div>
				<div style="display: flex; align-items: center; justify-content: space-between; margin-top: 4px; font-size: 11px; color: var(--c-text-muted);">
					<span>App ID: <code style="font-family: var(--font-mono); color: var(--c-cyan);">${escapeHtml(mintedSecret.identifier)}</code></span>
					<button id="dismiss-key" class="reg-btn-ghost" style="padding: 4px 10px; font-size: 11px;">Dismiss</button>
				</div>
			</div>
		`
			: "";

		const cardsHtml =
			apps.length === 0
				? `<div class="reg-empty" style="padding: 40px; text-align: center; color: var(--c-text-muted); font-style: italic;">No integration keys created.</div>`
				: apps
						.map((a) => {
							const isSelected = a.id === selectedAppId;
							return `
		<div class="app-card app-card-clickable ${isSelected ? "selected" : ""} ${a.status === "revoked" ? "revoked" : ""}" data-select-app="${escapeHtml(a.id)}" style="background: var(--c-surface); border: 1px solid var(--c-border); border-radius: var(--radius); padding: 16px; display: flex; flex-direction: column; gap: 12px; position: relative; ${a.status === "revoked" ? "opacity: 0.55;" : ""}">
			<div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 12px;">
				<div>
					<div style="font-family: var(--font-mono); font-weight: 700; font-size: 13px; color: var(--c-cyan); word-break: break-all;">${escapeHtml(a.app_identifier)}</div>
					<div style="font-size: 12px; color: var(--c-text-muted); margin-top: 2px;">${escapeHtml(a.label || "No description")}</div>
				</div>
				<span class="reg-pill ${a.status === "active" ? "green" : "gray"}" style="margin-top: 2px;">${escapeHtml(a.status)}</span>
			</div>
			
			<div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; font-size: 11px; border-top: 1px solid var(--c-border); padding-top: 12px;">
				<div>
					<span style="color: var(--c-text-muted); display: block; font-size: 9px; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px;">Key Prefix</span>
					<code style="font-family: var(--font-mono); font-size: 11px; background: rgba(0,0,0,0.2); padding: 2px 6px; border-radius: 4px; border: 1px solid var(--c-border); display: inline-block; margin-top: 2px;">${escapeHtml(a.key_prefix)}</code>
				</div>
				<div>
					<span style="color: var(--c-text-muted); display: block; font-size: 9px; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px;">Created</span>
					<span style="font-weight: 500; display: inline-block; margin-top: 4px;">${fmtDate(a.created_at)}</span>
				</div>
			</div>
			
			<div style="display: flex; justify-content: space-between; align-items: center; border-top: 1px solid var(--c-border); padding-top: 12px; font-size: 11px; color: var(--c-text-muted);">
				<span>Click to view details & manage</span>
				<span style="color: var(--c-cyan); font-weight: 600;">Details &rarr;</span>
			</div>
		</div>
		`;
						})
						.join("");

		let rightCardHtml = "";
		if (selectedAppId) {
			const selectedApp = apps.find((a) => a.id === selectedAppId);
			if (selectedApp) {
				const fetchedKey = fetchedKeys[selectedApp.id];
				const keyBoxHtml = fetchedKey
					? `
			<div style="background: rgba(34,197,94,.08); border: 1px solid rgba(34,197,94,.3); border-radius: var(--radius); padding: 12px; margin-top: 8px; display: flex; flex-direction: column; gap: 8px;">
				<span style="font-size: 11px; color: var(--c-text-muted); text-transform: uppercase; font-weight: 700; letter-spacing: 0.5px;">Integration Key Secret</span>
				<code style="font-family: var(--font-mono); font-size: 12px; word-break: break-all; background: rgba(0,0,0,0.2); padding: 4px 8px; border-radius: 4px; border: 1px solid var(--c-border);">${escapeHtml(fetchedKey)}</code>
				<button id="copy-fetched-key" class="reg-btn" style="background: var(--c-green); border-color: var(--c-green); color: #fff; padding: 4px 8px; font-size: 11px; margin-top: 4px; width: 100%;">Copy Key</button>
			</div>
			`
					: "";

				rightCardHtml = `
		<button id="btn-show-mint" class="reg-btn-ghost" style="width: 100%; margin-bottom: 12px; display: flex; align-items: center; justify-content: center; gap: 6px;">
			<span>&larr; Mint New App Key</span>
		</button>
		<div class="reg-card">
			<div class="reg-card-head">App Details</div>
			<div class="reg-card-body" style="display: flex; flex-direction: column; gap: 12px;">
			<div>
				<span style="color: var(--c-text-muted); display: block; font-size: 9px; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px;">App Identifier</span>
				<strong style="font-family: var(--font-mono); font-size: 13px; color: var(--c-cyan); word-break: break-all;">${escapeHtml(selectedApp.app_identifier)}</strong>
			</div>
			<div>
				<span style="color: var(--c-text-muted); display: block; font-size: 9px; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px;">Label / Description</span>
				<span style="font-size: 12px;">${escapeHtml(selectedApp.label || "No description")}</span>
			</div>
			<div>
				<span style="color: var(--c-text-muted); display: block; font-size: 9px; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px;">Key Prefix</span>
				<code style="font-family: var(--font-mono); font-size: 11px; background: rgba(0,0,0,0.2); padding: 2px 4px; border-radius: 4px; border: 1px solid var(--c-border); display: inline-block;">${escapeHtml(selectedApp.key_prefix)}</code>
			</div>
			
			${keyBoxHtml}

			${
				selectedApp.status === "active"
					? `
			<div style="border-top: 1px solid var(--c-border); padding-top: 12px; margin-top: 12px; display: flex; flex-direction: column; gap: 8px;">
				<button id="btn-fetch-key" class="reg-btn-ghost" style="width: 100%; font-size: 12px; padding: 6px 12px;">Fetch Key</button>
				<button id="btn-reroll-key" class="reg-btn-ghost" style="border-color: rgba(245,158,11,.4); color: var(--c-amber); width: 100%; font-size: 12px; padding: 6px 12px;">Reroll Key</button>
				<button id="btn-delete-app" class="reg-btn-danger" style="width: 100%; font-size: 12px; padding: 6px 12px;">Delete App</button>
			</div>
			`
					: `
			<div style="text-align: center; color: var(--c-text-muted); font-size: 12px; font-style: italic; border-top: 1px solid var(--c-border); padding-top: 12px; margin-top: 12px;">
				This app key has been revoked.
			</div>
			`
			}
			</div>
		</div>
		`;
			}
		}

		if (!rightCardHtml) {
			rightCardHtml = `
		<div class="reg-card">
		<div class="reg-card-head">Mint New Key</div>
		<div class="reg-card-body">
			<form class="reg-form" id="apps-form">
			<div class="reg-field">
				<label>App Identifier *</label>
				<input class="reg-input" id="app-ident" placeholder="com.company.shuttle" required>
			</div>
			<div class="reg-field">
				<label>Label / Description</label>
				<input class="reg-input" id="app-label" placeholder="e.g. Production Mobile App">
			</div>
			<div class="reg-field">
				<label>Environment</label>
				<select class="reg-select" id="app-env">
				<option value="live">Live</option>
				<option value="test">Test</option>
				</select>
			</div>
			<div class="reg-actions" style="margin-top: 8px;">
				<button type="submit" class="reg-btn" style="width: 100%;">Mint Key</button>
			</div>
			<p class="reg-hint">Minting creates a cryptographically secure integration key scoped to this tenant.</p>
			</form>
		</div>
		</div>
	`;
		}

		let modalHtml = "";
		if (activeModal) {
			const selectedApp = apps.find((a) => a.id === selectedAppId);
			if (selectedApp) {
				const isDestructive =
					activeModal === "reroll" || activeModal === "delete";
				const title =
					activeModal === "fetch"
						? "Fetch Integration Key"
						: activeModal === "reroll"
							? "Reroll Integration Key"
							: "Delete App Integration Key";
				const buttonText =
					activeModal === "fetch"
						? "Fetch Key"
						: activeModal === "reroll"
							? "Reroll Key"
							: "Delete App";
				const actionBtnClass = isDestructive ? "reg-btn-danger" : "reg-btn";

				const description =
					activeModal === "fetch"
						? `To view the integration key for <strong style="color:var(--c-cyan);">${escapeHtml(selectedApp.app_identifier)}</strong>, please enter your admin password.`
						: activeModal === "reroll"
							? `<span style="color:var(--c-amber); font-weight:700;">WARNING:</span> Rerolling the key will invalidate the current key immediately. Apps using it will fail to connect. Enter your admin password and the current integration key to proceed.`
							: `<span style="color:var(--c-red); font-weight:700;">CRITICAL:</span> Removing this app is permanent. The integration key will be revoked immediately and irreversibly. Enter your admin password and the current integration key to proceed.`;

				modalHtml = `
		<div class="reg-modal-backdrop" id="modal-backdrop">
			<div class="reg-modal">
			<div class="reg-modal-head">
				<span>${title}</span>
				<button class="tn-x" id="modal-close" style="background:none;border:none;color:var(--c-text-muted);font-size:20px;cursor:pointer;">&times;</button>
			</div>
			<form id="modal-confirm-form">
				<div class="reg-modal-body">
				<p style="font-size: 13px; margin: 0 0 4px; line-height: 1.4;">${description}</p>
				
				${
					modalError
						? `
					<div style="color: var(--c-red); background: rgba(239,68,68,.15); border: 1px solid rgba(239,68,68,.3); padding: 8px 12px; border-radius: var(--radius); font-size: 12px; font-weight: 500;">
					${escapeHtml(modalError)}
					</div>
				`
						: ""
				}
				
				<div class="reg-field">
					<label>Admin Password *</label>
					<input class="reg-input" type="password" id="modal-pass-input" placeholder="Your account password" required autofocus>
				</div>
				
				${
					isDestructive
						? `
					<div class="reg-field">
					<label>Current Integration Key *</label>
					<input class="reg-input" type="text" id="modal-key-input" placeholder="e.g. sk_live_..." required>
					</div>
				`
						: ""
				}
				</div>
				<div class="reg-modal-foot">
				<button type="button" class="reg-btn-ghost" id="modal-cancel-btn">Cancel</button>
				<button type="submit" class="${actionBtnClass}">${buttonText}</button>
				</div>
			</form>
			</div>
		</div>
		`;
			}
		}

		root.innerHTML = `
		<div class="panel-header"><h3>App Registration & Key Management</h3></div>
		${bannerHtml}
		${modalHtml}
		<div class="reg-kpi-bar" style="margin-bottom: var(--gap); grid-template-columns: repeat(2, 1fr); gap: var(--gap);">
		<div class="reg-kpi" style="display: flex; flex-direction: column; justify-content: center; min-height: 70px;">
			<div class="reg-kpi-label">Total Keys</div>
			<div class="reg-kpi-value">${apps.length}</div>
		</div>
		<div class="reg-kpi" style="display: flex; flex-direction: column; justify-content: center; min-height: 70px;">
			<div class="reg-kpi-label">Active Keys</div>
			<div class="reg-kpi-value" style="color: var(--c-green);">${apps.filter((a) => a.status === "active").length}</div>
		</div>
		</div>
		<div class="reg-grid" style="grid-template-columns: minmax(0, 1fr) 350px;">
		<div class="reg-card">
			<div class="reg-card-head">Integration Keys <span class="reg-hint">${apps.length} total</span></div>
			<div class="reg-card-body" style="padding: 16px; display: flex; flex-direction: column; gap: 12px; overflow-y: auto; max-height: 500px;">
			${cardsHtml}
			</div>
		</div>
		<div style="display: flex; flex-direction: column; gap: var(--gap);">
			${rightCardHtml}
		</div>
		</div>`;

		// Wire event listeners
		root.querySelectorAll<HTMLElement>("[data-select-app]").forEach((card) => {
			card.addEventListener("click", (e) => {
				const target = e.target as HTMLElement;
				if (target.closest("button")) {
					return;
				}
				const id = card.dataset.selectApp!;
				selectedAppId = id;
				renderUI();
			});
		});

		root.querySelector("#btn-show-mint")?.addEventListener("click", () => {
			selectedAppId = null;
			renderUI();
		});

		root.querySelector("#btn-fetch-key")?.addEventListener("click", () => {
			activeModal = "fetch";
			modalError = "";
			renderUI();
		});

		root.querySelector("#btn-reroll-key")?.addEventListener("click", () => {
			activeModal = "reroll";
			modalError = "";
			renderUI();
		});

		root.querySelector("#btn-delete-app")?.addEventListener("click", () => {
			activeModal = "delete";
			modalError = "";
			renderUI();
		});

		root.querySelector("#copy-fetched-key")?.addEventListener("click", () => {
			if (!selectedAppId) return;
			const key = fetchedKeys[selectedAppId];
			if (key) {
				navigator.clipboard?.writeText(key).then(() => {
					toast("Key copied to clipboard");
				});
			}
		});

		const form = root.querySelector("#apps-form") as HTMLFormElement | null;
		form?.addEventListener("submit", async (e) => {
			e.preventDefault();
			const ident = (
				root.querySelector("#app-ident") as HTMLInputElement
			).value.trim();
			const label = (
				root.querySelector("#app-label") as HTMLInputElement
			).value.trim();
			const env = (root.querySelector("#app-env") as HTMLSelectElement).value as
				| "live"
				| "test";
			if (!ident) {
				toast("App identifier is required", "err");
				return;
			}
			try {
				const created = await api.createApp(slug, {
					app_identifier: ident,
					label,
					env,
				});
				mintedSecret = {
					key: created.key_secret,
					appId: created.app.id,
					identifier: ident,
				};
				selectedAppId = created.app.id; // Auto select the new app
				toast("Key minted successfully");
				await loadAndRender();
			} catch (err) {
				toast((err as Error).message, "err");
			}
		});

		const modalForm = root.querySelector(
			"#modal-confirm-form",
		) as HTMLFormElement | null;
		modalForm?.addEventListener("submit", async (e) => {
			e.preventDefault();
			const password =
				(root.querySelector("#modal-pass-input") as HTMLInputElement)?.value ||
				"";
			const integrationKey =
				(root.querySelector("#modal-key-input") as HTMLInputElement)?.value ||
				"";

			if (!password) {
				modalError = "Password is required";
				renderUI();
				return;
			}

			const id = selectedAppId!;
			try {
				if (activeModal === "fetch") {
					const res = await api.fetchAppKey(slug, id, password);
					if (res.key_secret) {
						fetchedKeys[id] = res.key_secret;
						toast("Integration key fetched");
					} else {
						toast(
							"Legacy key cannot be retrieved. Reroll key to generate a new one.",
							"err",
						);
					}
					activeModal = null;
					renderUI();
				} else if (activeModal === "reroll") {
					if (!integrationKey) {
						modalError = "Current integration key is required";
						renderUI();
						return;
					}
					const res = await api.rerollAppKey(
						slug,
						id,
						password,
						integrationKey,
					);
					fetchedKeys[id] = res.key_secret;
					toast("Key rerolled successfully");
					activeModal = null;
					await loadAndRender();
				} else if (activeModal === "delete") {
					if (!integrationKey) {
						modalError = "Current integration key is required";
						renderUI();
						return;
					}
					await api.deleteAppSecure(slug, id, password, integrationKey);
					toast("App deleted successfully");
					selectedAppId = null;
					activeModal = null;
					await loadAndRender();
				}
			} catch (err) {
				modalError = (err as Error).message;
				renderUI();
			}
		});

		const closeModal = () => {
			activeModal = null;
			modalError = "";
			renderUI();
		};
		root.querySelector("#modal-close")?.addEventListener("click", closeModal);
		root
			.querySelector("#modal-cancel-btn")
			?.addEventListener("click", closeModal);
		root.querySelector("#modal-backdrop")?.addEventListener("click", (e) => {
			if (e.target === e.currentTarget) {
				closeModal();
			}
		});

		root.querySelector("#copy-key")?.addEventListener("click", () => {
			if (!mintedSecret) return;
			navigator.clipboard?.writeText(mintedSecret.key).then(() => {
				toast("Key copied to clipboard");
			});
		});

		root.querySelector("#dismiss-key")?.addEventListener("click", () => {
			mintedSecret = null;
			renderUI();
		});
	};

	await loadAndRender();
}

function escapeHtml(s: string): string {
	return s.replace(
		/[&<>"']/g,
		(c) =>
			({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
				c
			]!,
	);
}
